import React, { useRef, useState, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '../../i18n/LanguageContext';

gsap.registerPlugin(ScrollTrigger);

/* ── 24 Tunisian gouvernorats ── */
const GOUVERNORATS = [
  'Ariana','Béja','Ben Arous','Bizerte','Gabès','Gafsa','Jendouba',
  'Kairouan','Kasserine','Kébili','Le Kef','Mahdia','La Manouba',
  'Médenine','Monastir','Nabeul','Sfax','Sidi Bouzid','Siliana',
  'Sousse','Tataouine','Tozeur','Tunis','Zaghouan',
];

const INITIAL = {
  firstName: '', lastName: '', company: '', taxId: '',
  email: '', phone: '', street: '', zip: '', city: '',
};

export default function ResellerForm() {
  const { t, lang } = useTranslation();
  const sectionRef = useRef(null);
  const headRef    = useRef(null);
  const formRef    = useRef(null);

  const [form, setForm]       = useState(INITIAL);
  const [status, setStatus]   = useState('idle'); // idle | submitting | success | error

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /* ── GSAP entrance ── */
  useLayoutEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      if (headRef.current) {
        gsap.fromTo(headRef.current,
          { opacity: 0, y: 50 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
            scrollTrigger: { trigger: headRef.current, start: 'top 85%' } });
      }
      if (formRef.current) {
        gsap.fromTo(formRef.current,
          { opacity: 0, y: 60 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: 0.15,
            scrollTrigger: { trigger: formRef.current, start: 'top 85%' } });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [lang]);

  /* ── submit handler → backend API (Resend email) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch('https://app.linxexpress.tn/reseller-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm(INITIAL);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  /* ── reusable input class ── */
  const inputCls =
    'w-full rounded-xl border border-primary/10 bg-linx-bg px-4 py-3 text-sm text-primary placeholder:text-primary/30 outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20 transition';

  return (
    <section
      id="reseller"
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20 items-start">

          {/* ── LEFT: heading + description ── */}
          <div ref={headRef}>
            <span className="mb-4 inline-block rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
              {t('reseller.label')}
            </span>
            <h2 className="mb-5 text-3xl font-bold leading-tight text-primary sm:text-4xl lg:text-5xl">
              {t('reseller.heading')}
            </h2>
            <p className="mb-8 text-base leading-relaxed text-primary/60 lg:text-lg">
              {t('reseller.desc')}
            </p>

            {/* benefit bullets */}
            <ul className="space-y-4 text-primary/70">
              {[
                lang === 'ar'
                  ? 'عمولة على كل عملية شحن'
                  : 'Commission sur chaque recharge',
                lang === 'ar'
                  ? 'مواد تسويقية ودعم فني'
                  : 'Matériel marketing & support technique',
                lang === 'ar'
                  ? 'لوحة تحكم لمتابعة أرباحك'
                  : 'Dashboard pour suivre vos gains',
              ].map((txt) => (
                <li key={txt} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-secondary" />
                  <span className="text-sm lg:text-base">{txt}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── RIGHT: form card ── */}
          <div
            ref={formRef}
            className="rounded-3xl border border-primary/[0.06] bg-linx-bg p-8 shadow-xl shadow-primary/[0.04] lg:p-10"
          >
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-primary">{t('reseller.success')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* name row */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.firstName')}</label>
                    <input required value={form.firstName} onChange={set('firstName')} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.lastName')}</label>
                    <input required value={form.lastName} onChange={set('lastName')} className={inputCls} />
                  </div>
                </div>

                {/* company + tax */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.company')}</label>
                    <input required value={form.company} onChange={set('company')} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.taxId')}</label>
                    <input required value={form.taxId} onChange={set('taxId')} className={inputCls} />
                  </div>
                </div>

                {/* email + phone */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.email')}</label>
                    <input required type="email" value={form.email} onChange={set('email')} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.phone')}</label>
                    <input required type="tel" value={form.phone} onChange={set('phone')} className={inputCls} />
                  </div>
                </div>

                {/* address heading */}
                <p className="pt-2 text-xs font-semibold uppercase tracking-widest text-primary/40">
                  {t('reseller.address')}
                </p>

                {/* street */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.street')}</label>
                  <input required value={form.street} onChange={set('street')} className={inputCls} />
                </div>

                {/* zip + gouvernorat */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.zip')}</label>
                    <input required value={form.zip} onChange={set('zip')} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-primary/60">{t('reseller.city')}</label>
                    <select required value={form.city} onChange={set('city')} className={inputCls}>
                      <option value="">{t('reseller.cityPlaceholder')}</option>
                      {GOUVERNORATS.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-4 w-full rounded-xl bg-secondary py-4 text-sm font-bold uppercase tracking-wider text-primary shadow-lg shadow-secondary/25 transition-all duration-300 hover:bg-secondary-light hover:shadow-xl hover:shadow-secondary/30 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {status === 'submitting' ? t('reseller.submitting') : t('reseller.submit')}
                </button>

                {status === 'error' && (
                  <p className="text-center text-sm text-red-500">{t('reseller.error')}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
